import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft,
  CalendarIcon,
  MessageSquare,
  Send,
  Loader2,
  Trash2,
  Edit2,
  MoreVertical,
  TrendingUp,
  History,
  Users,
  Plus,
  ClipboardList,
  Check,
  ChevronRight,
  Target,
  Infinity as InfinityIcon,
  Link2,
  CalendarPlus
} from 'lucide-react';
import AttachmentSection from '../components/tasks/AttachmentSection';
import { DriveLinksEditor } from '../components/tasks/DriveLinks';
import {
  DEFAULT_NUMERIC_CONFIG,
  NUMERICAL_CURRENCY_OPTIONS,
  buildNumericConfigPayload,
  buildWeeklyUpdatePayload,
  clampProgressPercent,
  formatNumericValue,
  formatProgressPercent,
  getContributionText,
  getDriveLinks,
  getNumericConfig,
  getNumericalProgressText,
  getProgressColorClass,
  getTaskTypeLabel,
  isNumericalTask,
  isOngoingTask,
  isPositiveDecimal,
  isProgressEditable,
  normalizeDecimalValue,
  normalizeTaskType,
  toNumericInputValue,
} from '../lib/taskTypes';

const TaskDetailPage = () => {
  const { api, user } = useAuth();
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleteCommentConfirm, setDeleteCommentConfirm] = useState({ open: false, commentId: null });
  const [deleteTaskConfirm, setDeleteTaskConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updateHistory, setUpdateHistory] = useState([]);
  const [progressForm, setProgressForm] = useState({ percent_complete: 0, notes: '', blockers: '' });
  const [showProgressForm, setShowProgressForm] = useState(false);
  const [submittingProgress, setSubmittingProgress] = useState(false);
  const [subtasks, setSubtasks] = useState([]);
  const [subtaskDialogOpen, setSubtaskDialogOpen] = useState(false);
  const [subtaskForm, setSubtaskForm] = useState({
    title: '', description: '', priority: 'medium',
    start_date: null, end_date: null, additional_assignees: [], contribution_value: ''
  });
  const [submittingSubtask, setSubmittingSubtask] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '', description: '', priority: 'medium',
    task_type: 'standard', numeric_config: { ...DEFAULT_NUMERIC_CONFIG },
    primary_assignee: '', contribution_value: '',
    secondary_assignee: '', additional_assignees: [],
    start_date: null, end_date: null,
    is_ongoing: false, drive_links: []
  });
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [editingSubtaskId, setEditingSubtaskId] = useState(null); // null = editing parent task, else subtask id

  // Delete subtask confirm
  const [deleteSubtaskConfirm, setDeleteSubtaskConfirm] = useState({ open: false, subtaskId: null });
  const [deletingSubtask, setDeletingSubtask] = useState(false);
  const [savingDriveLinks, setSavingDriveLinks] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extending, setExtending] = useState(false);

  const isSubtask = !!(task && task.parent_task);
  const taskIsNumerical = isNumericalTask(task);
  const progressEditable = task
    ? !taskIsNumerical && isProgressEditable(task, isSubtask ? undefined : subtasks.length)
    : false;
  const taskIsOngoing = isOngoingTask(task);
  // Ongoing tasks still take weekly notes/blockers, just never a percentage.
  const canSubmitWeeklyUpdate = progressEditable || (taskIsNumerical && !isSubtask) || taskIsOngoing;

  useEffect(() => {
    // Reset all form/dialog states when navigating between tasks
    setShowProgressForm(false);
    setEditDialogOpen(false);
    setSubtaskDialogOpen(false);
    setDeleteSubtaskConfirm({ open: false, subtaskId: null });
    setDeleteTaskConfirm(false);
    setDeleteCommentConfirm({ open: false, commentId: null });
    setNewComment('');
    fetchData();
  }, [taskId]);

  const fetchData = async () => {
    try {
      const requests = [
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/comments`).catch(() => ({ data: [] })),
        api.get(`/weekly-updates/task/${taskId}/history`).catch(() => ({ data: [] })),
        api.get(`/tasks/${taskId}/subtasks`).catch(() => ({ data: [] })),
        api.get('/users').catch(() => ({ data: [] }))
      ];
      const [taskRes, commentsRes, historyRes, subtasksRes, usersRes] = await Promise.all(requests);
      setTask(taskRes.data);
      setComments(Array.isArray(commentsRes.data) ? commentsRes.data : []);
      setUpdateHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
      setSubtasks(Array.isArray(subtasksRes.data) ? subtasksRes.data : []);
      const allUsers = Array.isArray(usersRes.data) ? usersRes.data : [];
      setActiveUsers(allUsers.filter(u => u.is_active !== false));
    } catch (error) {
      toast.error('Failed to load task');
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/tasks/${taskId}/comments`, { content: newComment });
      setNewComment('');
      toast.success('Comment added');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async () => {
    const commentId = deleteCommentConfirm.commentId;
    if (!commentId) return;
    setDeleting(true);
    try {
      await api.delete(`/comments/${commentId}`);
      toast.success('Comment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete comment');
    } finally {
      setDeleting(false);
      setDeleteCommentConfirm({ open: false, commentId: null });
    }
  };

  const handleDeleteTask = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success(isSubtask ? 'Subtask deleted' : 'Task deleted');
      if (isSubtask && task.parent_task) {
        navigate(`/tasks/${task.parent_task}`);
      } else {
        navigate('/tasks');
      }
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
      setDeleteTaskConfirm(false);
    }
  };

  const handleProgressSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmitWeeklyUpdate) {
      toast.error('Progress for this task is calculated automatically');
      setShowProgressForm(false);
      return;
    }
    const pct = Math.min(100, Math.max(0, parseInt(progressForm.percent_complete) || 0));
    setSubmittingProgress(true);
    try {
      const payload = buildWeeklyUpdatePayload(task, {
        ...progressForm,
        percent_complete: pct,
      });
      await api.post('/weekly-updates', payload);
      toast.success(taskIsNumerical || taskIsOngoing ? 'Weekly update submitted' : 'Progress updated');
      setShowProgressForm(false);
      setProgressForm({ percent_complete: pct, notes: '', blockers: '' });
      fetchData();
    } catch (error) {
      toast.error(taskIsNumerical || taskIsOngoing ? 'Failed to submit weekly update' : 'Failed to update progress');
    } finally {
      setSubmittingProgress(false);
    }
  };

  const openSubtaskDialog = () => {
    setSubtaskForm({
      title: '', description: '', priority: 'medium',
      start_date: null, end_date: null, additional_assignees: [], contribution_value: ''
    });
    setSubtaskDialogOpen(true);
  };

  const toggleSubtaskAssignee = (userId) => {
    setSubtaskForm(prev => {
      const current = prev.additional_assignees || [];
      if (current.includes(userId)) {
        return { ...prev, additional_assignees: current.filter(id => id !== userId) };
      } else {
        return { ...prev, additional_assignees: [...current, userId] };
      }
    });
  };

  const handleSubtaskSubmit = async (e) => {
    e.preventDefault();
    if (!subtaskForm.title.trim()) return;
    if (!subtaskForm.additional_assignees || subtaskForm.additional_assignees.length === 0) {
      toast.error('Please assign at least one person to the subtask');
      return;
    }
    if (taskIsNumerical && !isPositiveDecimal(subtaskForm.contribution_value)) {
      toast.error('Contribution amount must be greater than zero and have at most 2 decimal places');
      return;
    }
    if (!subtaskForm.start_date) {
      toast.error('Start date is required');
      return;
    }
    if (!subtaskForm.end_date) {
      toast.error('End date is required');
      return;
    }
    setSubmittingSubtask(true);
    try {
      const payload = {
        title: subtaskForm.title.trim(),
        description: subtaskForm.description,
        priority: subtaskForm.priority,
        start_date: subtaskForm.start_date ? subtaskForm.start_date.toISOString() : null,
        end_date: subtaskForm.end_date ? subtaskForm.end_date.toISOString() : null,
        additional_assignees: subtaskForm.additional_assignees,
        parent_task: taskId,
      };
      if (taskIsNumerical) {
        payload.contribution_value = normalizeDecimalValue(subtaskForm.contribution_value);
      }
      await api.post('/tasks', payload);
      toast.success('Subtask created');
      setSubtaskDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create subtask');
    } finally {
      setSubmittingSubtask(false);
    }
  };

  const saveDriveLinks = async (links) => {
    setSavingDriveLinks(true);
    try {
      const res = await api.put(`/tasks/${taskId}`, { drive_links: links });
      setTask(prev => ({ ...prev, drive_links: res.data?.drive_links ?? links }));
      toast.success('Drive links updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update Drive links');
    } finally {
      setSavingDriveLinks(false);
    }
  };

  const extendTillDate = async (date) => {
    if (!date) return;
    if (task.start_date && date < new Date(task.start_date)) {
      toast.error('Till date must be on or after start date');
      return;
    }
    setExtending(true);
    try {
      await api.put(`/tasks/${taskId}`, { end_date: date.toISOString() });
      toast.success(`Till date set to ${format(date, 'MMM d, yyyy')}`);
      setExtendOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to extend till date');
    } finally {
      setExtending(false);
    }
  };

  // --- Edit Dialog logic ---
  const openEditDialog = (taskData, subtaskIdOverride) => {
    const isEditingSubtask = !!subtaskIdOverride;
    const numericConfig = getNumericConfig(taskData);
    setEditingSubtaskId(subtaskIdOverride || null);
    setEditFormData({
      title: taskData.title || '',
      description: taskData.description || '',
      priority: taskData.priority || 'medium',
      task_type: normalizeTaskType(taskData.task_type),
      numeric_config: {
        ...numericConfig,
        target_value: toNumericInputValue(numericConfig.target_value),
      },
      primary_assignee: isEditingSubtask ? '' : (taskData.primary_assignee || ''),
      contribution_value: toNumericInputValue(taskData.contribution_value),
      secondary_assignee: isEditingSubtask ? '' : (taskData.secondary_assignee || ''),
      additional_assignees: taskData.additional_assignees || [],
      start_date: taskData.start_date ? new Date(taskData.start_date) : null,
      end_date: taskData.end_date ? new Date(taskData.end_date) : null,
      is_ongoing: isOngoingTask(taskData),
      drive_links: getDriveLinks(taskData),
    });
    setEditDialogOpen(true);
  };

  const toggleEditAssignee = (userId) => {
    setEditFormData(prev => {
      const current = prev.additional_assignees || [];
      if (current.includes(userId)) {
        return { ...prev, additional_assignees: current.filter(id => id !== userId) };
      } else {
        return { ...prev, additional_assignees: [...current, userId] };
      }
    });
  };

  // Primary assignee ID for the task being edited (for filtering dropdowns)
  const editPrimaryAssigneeId = editingSubtaskId ? null : (editFormData.primary_assignee || task?.primary_assignee || null);
  const editOwnerOptions = activeUsers.concat(
    (task?.primary_assignee && !activeUsers.some(u => u.id === task.primary_assignee))
      ? [{ id: task.primary_assignee, name: task.primary_assignee_name || 'Inactive user', is_active: false }]
      : [],
    (task?.secondary_assignee && !activeUsers.some(u => u.id === task.secondary_assignee) && task.secondary_assignee !== task.primary_assignee)
      ? [{ id: task.secondary_assignee, name: task.secondary_assignee_name || 'Inactive user', is_active: false }]
      : []
  );

  // Users available for "Assigned To" multi-select in edit dialog (exclude primary + secondary)
  const editAvailableForAssignment = activeUsers.filter(u => {
    if (editPrimaryAssigneeId && u.id === editPrimaryAssigneeId) return false;
    if (!editingSubtaskId && editFormData.secondary_assignee && editFormData.secondary_assignee !== 'none' && u.id === editFormData.secondary_assignee) return false;
    return true;
  });

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.title.trim()) return;

    if (!editingSubtaskId && !editFormData.primary_assignee) {
      toast.error('Primary owner is required');
      return;
    }
    if (!editingSubtaskId && isNumericalTask(editFormData.task_type) && !isPositiveDecimal(editFormData.numeric_config?.target_value)) {
      toast.error('Target amount must be greater than zero and have at most 2 decimal places');
      return;
    }
    if (editingSubtaskId && isNumericalTask(editFormData.task_type) && !isPositiveDecimal(editFormData.contribution_value)) {
      toast.error('Contribution amount must be greater than zero and have at most 2 decimal places');
      return;
    }

    // Validate dates are mandatory
    if (!editFormData.start_date) {
      toast.error('Start date is required');
      return;
    }
    const endLabel = editFormData.is_ongoing ? 'Till date' : 'End date';
    if (!editFormData.end_date) {
      toast.error(`${endLabel} is required`);
      return;
    }

    // Validate end_date >= start_date
    if (editFormData.start_date && editFormData.end_date && new Date(editFormData.end_date) < new Date(editFormData.start_date)) {
      toast.error(`${endLabel} must be on or after start date`);
      return;
    }

    setSubmittingEdit(true);
    try {
      const targetId = editingSubtaskId || taskId;
      const payload = {
        title: editFormData.title.trim(),
        description: editFormData.description,
        priority: editFormData.priority,
        start_date: editFormData.start_date ? editFormData.start_date.toISOString() : null,
        end_date: editFormData.end_date ? editFormData.end_date.toISOString() : null,
        additional_assignees: editFormData.additional_assignees || [],
        drive_links: editFormData.drive_links || [],
      };

      // For parent tasks, include secondary_assignee
      if (!editingSubtaskId) {
        if (editFormData.primary_assignee !== task?.primary_assignee) {
          payload.primary_assignee = editFormData.primary_assignee;
        }
        const secondaryAssignee = editFormData.secondary_assignee && editFormData.secondary_assignee !== 'none'
          ? editFormData.secondary_assignee
          : null;
        if (secondaryAssignee !== (task?.secondary_assignee || null)) {
          payload.secondary_assignee = secondaryAssignee;
        }
        payload.task_type = normalizeTaskType(editFormData.task_type);
        if (isNumericalTask(editFormData.task_type)) {
          payload.numeric_config = buildNumericConfigPayload(editFormData.numeric_config);
        } else {
          payload.is_ongoing = !!editFormData.is_ongoing;
        }
      } else if (isNumericalTask(editFormData.task_type)) {
        payload.contribution_value = normalizeDecimalValue(editFormData.contribution_value);
      }

      const editedTask = editingSubtaskId
        ? (String(editingSubtaskId) === String(taskId)
          ? task
          : subtasks.find(s => String(s.id) === String(editingSubtaskId)))
        : task;
      if (!editFormData.is_ongoing && !isOngoingTask(editedTask) && isProgressEditable(editedTask, editingSubtaskId ? undefined : subtasks.length)) {
        payload.percent_complete = (editingSubtaskId
          ? (editedTask?.percent_complete || 0)
          : (task?.percent_complete || 0));
      }

      await api.put(`/tasks/${targetId}`, payload);
      toast.success(editingSubtaskId ? 'Subtask updated' : 'Task updated');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update');
    } finally {
      setSubmittingEdit(false);
    }
  };

  // --- Delete subtask logic ---
  const handleDeleteSubtask = async () => {
    const subtaskId = deleteSubtaskConfirm.subtaskId;
    if (!subtaskId) return;
    setDeletingSubtask(true);
    try {
      await api.delete(`/tasks/${subtaskId}`);
      toast.success('Subtask deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete subtask');
    } finally {
      setDeletingSubtask(false);
      setDeleteSubtaskConfirm({ open: false, subtaskId: null });
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'warning';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'default';
      case 'pending': return 'pending';
      default: return 'outline';
    }
  };

  const safeFormat = (dateStr, fmt) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return 'N/A';
      return format(d, fmt);
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="task-detail-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Task not found</p>
        <Button variant="link" onClick={() => navigate('/tasks')}>&#8592; Back to Tasks</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="task-detail-page">
      {/* Header */}
      <div className="space-y-1.5">
        <div className="flex items-start gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => isSubtask ? navigate(`/tasks/${task.parent_task}`) : navigate('/tasks')}
            data-testid="back-btn"
            className="shrink-0 h-8 w-8 mt-0.5"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            {isSubtask && (
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">SUBTASK</p>
            )}
            <h1 className="text-lg md:text-3xl tracking-tight uppercase break-words">{task.title}</h1>
            <div className="flex items-center gap-0.5 mt-1">
              <Badge variant={getPriorityColor(task.priority)} className="text-[9px] sm:text-[10px] px-1.5 py-0">
                {task.priority.toUpperCase()}
              </Badge>
              <Badge variant={getStatusColor(task.status)} className="text-[9px] sm:text-[10px] px-1.5 py-0 whitespace-nowrap">
                {task.status.replace('_', ' ').toUpperCase()}
              </Badge>
              {taskIsNumerical && (
                <Badge variant="outline" className="text-[9px] sm:text-[10px] px-1.5 py-0 gap-1">
                  <Target className="h-3 w-3" />
                  NUMERICAL
                </Badge>
              )}
              {taskIsOngoing && (
                <Badge variant="outline" className="text-[9px] sm:text-[10px] px-1.5 py-0 gap-1 border-primary/40 text-primary" data-testid="ongoing-badge">
                  <InfinityIcon className="h-3 w-3" />
                  ONGOING
                </Badge>
              )}
              {task.till_date_passed && (
                <Badge variant="outline" className="text-[9px] sm:text-[10px] px-1.5 py-0 border-orange-500/40 text-orange-500" data-testid="till-date-passed-badge">
                  TILL DATE PASSED
                </Badge>
              )}
              {task.is_overdue && (
                <Badge variant="destructive" className="text-[9px] sm:text-[10px] px-1.5 py-0" data-testid="overdue-badge">
                  OVERDUE
                </Badge>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openEditDialog(task, isSubtask ? taskId : null)} data-testid="edit-task-btn">
                <Edit2 className="h-3.5 w-3.5 mr-2" />
                Edit {isSubtask ? 'Subtask' : 'Task'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setDeleteTaskConfirm(true)}
                data-testid="delete-task-btn"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Delete {isSubtask ? 'Subtask' : 'Task'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Description */}
      {task.description ? (
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
              DESCRIPTION
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{task.description}</p>
          </CardContent>
        </Card>
      ) : null}

      {/* Details */}
      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
            DETAILS
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 items-start">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Status</p>
              <p className="text-xs h-7 leading-7">
                <Badge variant={getStatusColor(task.status)} className="text-[10px]">
                  {task.status.replace('_', ' ').toUpperCase()}
                </Badge>
              </p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Task Type</p>
              <p className="text-xs h-7 leading-7">{getTaskTypeLabel(task)}</p>
            </div>
            {!isSubtask && task.primary_assignee_name && (
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Primary Owner</p>
                <p className="text-xs h-7 leading-7">{task.primary_assignee_name}</p>
              </div>
            )}
            {!isSubtask && task.secondary_assignee_name && (
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Secondary Owner</p>
                <p className="text-xs h-7 leading-7">{task.secondary_assignee_name}</p>
              </div>
            )}
            {task.additional_assignee_names && task.additional_assignee_names.length > 0 && (
              <div className="col-span-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
                  {isSubtask ? 'Assigned To' : 'Assigned To'}
                </p>
                <div className="flex flex-wrap gap-1">
                  {task.additional_assignee_names.map((name, i) => (
                    <Badge key={i} variant="outline" className="text-[10px]">{name}</Badge>
                  ))}
                </div>
              </div>
            )}
            {!isSubtask && task.additional_assignee_names && task.additional_assignee_names.length === 0 && task.assigned_names && task.assigned_names.length > 0 && (
              <div className="col-span-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Assigned To</p>
                <div className="flex flex-wrap gap-1">
                  {task.assigned_names.map((name, i) => (
                    <Badge key={i} variant="outline" className="text-[10px]">{name}</Badge>
                  ))}
                </div>
              </div>
            )}
            {task.start_date && (
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Start</p>
                <p className="text-xs h-7 leading-7">{safeFormat(task.start_date, 'MMM d, yyyy')}</p>
              </div>
            )}
            {task.end_date && (
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{taskIsOngoing ? 'Till Date' : 'End'}</p>
                <div className="flex items-center gap-1 h-7">
                  <p className={`text-xs leading-7 ${task.till_date_passed ? 'text-orange-500 font-medium' : ''}`}>
                    {safeFormat(task.end_date, 'MMM d, yyyy')}
                  </p>
                  {taskIsOngoing && (
                    <Popover open={extendOpen} onOpenChange={setExtendOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" disabled={extending} data-testid="extend-till-date-btn">
                          {extending ? <Loader2 className="h-3 w-3 animate-spin" /> : <CalendarPlus className="h-3 w-3 mr-1" />}
                          EXTEND
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={new Date(task.end_date)}
                          defaultMonth={new Date(task.end_date)}
                          onSelect={extendTillDate}
                          disabled={{ before: task.start_date ? new Date(task.start_date) : new Date() }}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>
            )}
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Created By</p>
              <p className="text-xs h-7 leading-7">{task.created_by_name || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Created</p>
              <p className="text-xs h-7 leading-7">{safeFormat(task.createdAt, 'MMM d, yyyy')}</p>
            </div>
            {taskIsNumerical && !isSubtask && (
              <>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Target</p>
                  <p className="text-xs h-7 leading-7">{formatNumericValue(getNumericConfig(task).target_value, getNumericConfig(task))}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Current</p>
                  <p className="text-xs h-7 leading-7">{formatNumericValue(getNumericConfig(task).current_value ?? 0, getNumericConfig(task))}</p>
                </div>
              </>
            )}
            {taskIsNumerical && isSubtask && (
              <div className="col-span-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Contribution</p>
                <p className="text-sm font-semibold h-7 leading-7">{getContributionText(task)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Progress Section */}
      {!(taskIsNumerical && isSubtask) && (
      <Card className="border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              PROGRESS
            </CardTitle>
            <div className="flex items-center gap-2">
              {!progressEditable && !taskIsOngoing && (
                <span className="text-[10px] text-muted-foreground">
                  {taskIsNumerical ? 'Calculated from contributions' : 'Calculated from subtasks'}
                </span>
              )}
              {canSubmitWeeklyUpdate && (
                <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => {
                  setProgressForm({ percent_complete: task.percent_complete || 0, notes: '', blockers: '' });
                  setShowProgressForm(!showProgressForm);
                }}>
                  {showProgressForm ? 'CANCEL' : 'UPDATE'}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {taskIsOngoing ? (
            <div className="flex items-start gap-2 p-3 border border-border bg-muted/20" data-testid="ongoing-progress-note">
              <InfinityIcon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground">
                Ongoing responsibility — percentage progress is not tracked.
                {task.end_date && ` Active till ${safeFormat(task.end_date, 'MMM d, yyyy')}.`}
                {task.till_date_passed && ' The till date has passed; use EXTEND to continue it.'}
              </p>
            </div>
          ) : (
          <div>
            {taskIsNumerical && (
              <p className="text-sm font-semibold mb-1" data-testid="numerical-progress-value">
                {getNumericalProgressText(task)}
              </p>
            )}
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium">
                {(task.percent_complete || 0) > 0
                  ? `${formatProgressPercent(task.percent_complete)}% Complete`
                  : <span className="text-muted-foreground">{taskIsNumerical ? 'No contributions yet' : 'Not tracked yet'}</span>
                }
              </span>
              {(task.percent_complete || 0) > 0 && (
                <span className="text-xs text-muted-foreground">{formatProgressPercent(task.percent_complete)}%</span>
              )}
            </div>
            <div className="h-2 bg-muted border border-border overflow-hidden">
              <div
                className={`h-full transition-all ${getProgressColorClass(task.percent_complete)}`}
                style={{ width: `${clampProgressPercent(task.percent_complete)}%` }}
              />
            </div>
            {(task.percent_complete || 0) === 0 && !showProgressForm && progressEditable && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Tap UPDATE to submit your first progress report.
              </p>
            )}
          </div>
          )}

          {showProgressForm && canSubmitWeeklyUpdate && (
            <form onSubmit={handleProgressSubmit} className="space-y-3 p-3 border border-border bg-muted/20">
              {taskIsOngoing ? null : taskIsNumerical ? (
                <div className="space-y-1.5">
                  <Label className="text-xs">CALCULATED PROGRESS</Label>
                  <output
                    className="h-10 flex items-center border border-input bg-muted px-3 text-sm"
                    aria-label="Calculated progress"
                    data-testid="task-detail-calculated-progress"
                  >
                    {formatProgressPercent(task.percent_complete)}%
                  </output>
                  <p className="text-[10px] text-muted-foreground">Updated automatically from subtask contributions.</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs">PERCENT COMPLETE</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={progressForm.percent_complete}
                    onChange={(e) => setProgressForm(prev => ({ ...prev, percent_complete: e.target.value }))}
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">NOTES</Label>
                <Textarea
                  value={progressForm.notes}
                  onChange={(e) => setProgressForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="What did you work on?"
                  rows={2}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">BLOCKERS</Label>
                <Textarea
                  value={progressForm.blockers}
                  onChange={(e) => setProgressForm(prev => ({ ...prev, blockers: e.target.value }))}
                  placeholder="Any blockers?"
                  rows={2}
                />
              </div>
              <Button type="submit" disabled={submittingProgress} size="sm" className="w-full">
                {submittingProgress ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TrendingUp className="h-4 w-4 mr-2" />}
                {taskIsNumerical || taskIsOngoing ? 'SUBMIT WEEKLY UPDATE' : 'SUBMIT PROGRESS'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      )}

      {/* Google Drive links — open in a new browser tab */}
      <Card className="border-border" data-testid="drive-links-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            GOOGLE DRIVE LINKS ({getDriveLinks(task).length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DriveLinksEditor
            value={getDriveLinks(task)}
            onChange={saveDriveLinks}
            disabled={savingDriveLinks}
            showLabel={false}
          />
        </CardContent>
      </Card>

      <AttachmentSection api={api} taskId={taskId} user={user} />

      {/* Subtasks Section — only for parent tasks, NOT for subtasks */}
      {!isSubtask && (
        <Card className="border-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <ClipboardList className="h-4 w-4" />
                SUBTASKS ({subtasks.length})
              </CardTitle>
              <Button variant="outline" size="sm" className="text-xs h-8" onClick={openSubtaskDialog}>
                <Plus className="h-3 w-3 mr-1" />
                ADD SUBTASK
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {subtasks.length > 0 ? (
              <div className="space-y-2">
                {subtasks.map(subtask => (
                  <div
                    key={subtask.id}
                    className="p-3 border border-border hover:border-primary/50 hover:bg-muted/30 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/tasks/${subtask.id}`)}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <h4 className="text-sm font-medium truncate">{subtask.title}</h4>
                      <div className="flex items-center gap-1 shrink-0">
                        <Badge variant={getPriorityColor(subtask.priority)} className="text-[9px] px-1.5 py-0">
                          {subtask.priority.toUpperCase()}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(subtask, subtask.id);
                          }}
                          data-testid={`edit-subtask-${subtask.id}`}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteSubtaskConfirm({ open: true, subtaskId: subtask.id });
                          }}
                          data-testid={`delete-subtask-${subtask.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isNumericalTask(subtask) ? (
                        <span className="text-xs font-semibold flex-1" data-testid={`subtask-contribution-${subtask.id}`}>
                          Contribution: {getContributionText(subtask, getNumericConfig(task))}
                        </span>
                      ) : (
                        <>
                          <div className="h-1.5 bg-muted border border-border overflow-hidden flex-1">
                            <div
                              className={`h-full transition-all ${getProgressColorClass(subtask.percent_complete)}`}
                              style={{ width: `${clampProgressPercent(subtask.percent_complete)}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-muted-foreground shrink-0">{formatProgressPercent(subtask.percent_complete)}%</span>
                        </>
                      )}
                      <Badge variant={getStatusColor(subtask.status)} className="text-[9px] px-1.5 py-0 shrink-0">
                        {subtask.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    {subtask.additional_assignee_names && subtask.additional_assignee_names.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mt-1.5">
                        <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Assigned to:</span>
                        {subtask.additional_assignee_names.map((name, i) => (
                          <Badge key={i} variant="outline" className="text-[9px] px-1.5 py-0">{name}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <ClipboardList className="h-6 w-6 text-muted-foreground/40 mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">No subtasks yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Update History — only for parent tasks */}
      {!isSubtask && updateHistory.length > 0 && (
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <History className="h-4 w-4" />
              HISTORY ({updateHistory.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {updateHistory.map(update => (
                <div key={update.id} className="p-3 border border-border rounded-sm">
                  <div className="flex items-start gap-2.5">
                    <div className="h-8 w-8 bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0 rounded-full">
                      {(update.user_name || 'T').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold truncate">{update.user_name || 'Team Member'}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {safeFormat(update.updatedAt || update.createdAt, 'MMM d, yyyy · h:mm a')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">
                          Updated progress to <span className="font-semibold text-foreground">{update.percent_complete}%</span>
                        </span>
                        {update.subtask_title && (
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0 shrink-0">
                            {update.subtask_title}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  {update.notes && (
                    <div className="ml-10 mt-2 p-2 bg-muted/30 rounded-sm">
                      <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-0.5">Notes</p>
                      <p className="text-xs text-foreground">{update.notes}</p>
                    </div>
                  )}
                  {update.blockers && (
                    <div className="ml-10 mt-1.5 p-2 bg-destructive/5 rounded-sm border border-destructive/20">
                      <p className="text-[10px] text-destructive font-medium uppercase tracking-wider mb-0.5">Blocker</p>
                      <p className="text-xs text-destructive">{update.blockers}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comments Section — only for parent tasks */}
      {!isSubtask && (
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              COMMENTS ({comments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <form onSubmit={handleAddComment} className="space-y-2">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                rows={2}
                className="w-full"
                data-testid="comment-input"
              />
              <Button
                type="submit"
                size="sm"
                disabled={submitting || !newComment.trim()}
                className="w-full sm:w-auto"
                data-testid="add-comment-btn"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                SEND
              </Button>
            </form>
            <div className="space-y-2">
              {comments.map(comment => (
                <div
                  key={comment.id}
                  className="p-3 border border-border rounded-sm bg-muted/10"
                  data-testid={`comment-${comment.id}`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className="h-8 w-8 bg-primary/15 flex items-center justify-center text-primary text-sm font-bold shrink-0 rounded-full">
                      {comment.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold leading-tight">{comment.user_name}</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {safeFormat(comment.createdAt, 'MMM d, yyyy · h:mm a')}
                          </p>
                        </div>
                        {(comment.user_id === user?.id || user?.role === 'admin') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0"
                            onClick={() => setDeleteCommentConfirm({ open: true, commentId: comment.id })}
                            data-testid={`delete-comment-${comment.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <p className="text-sm mt-2 whitespace-pre-wrap">{comment.content}</p>
                    </div>
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <div className="text-center py-6">
                  <MessageSquare className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No comments yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Subtask Dialog — full modal like task creation */}
      <Dialog open={subtaskDialogOpen} onOpenChange={setSubtaskDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>ADD SUBTASK</DialogTitle>
            <DialogDescription>
              {taskIsNumerical ? 'Add a numerical contribution under this task' : 'Create a new subtask under this task'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubtaskSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>TITLE</Label>
              <Input
                value={subtaskForm.title}
                onChange={(e) => setSubtaskForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Subtask title"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>DESCRIPTION</Label>
              <Textarea
                value={subtaskForm.description}
                onChange={(e) => setSubtaskForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
                rows={3}
              />
            </div>
            {taskIsNumerical && (
              <div className="space-y-2">
                <Label>CONTRIBUTION AMOUNT <span className="text-destructive">*</span></Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  pattern={"\\d+(\\.\\d{1,2})?"}
                  value={subtaskForm.contribution_value}
                  onChange={(e) => setSubtaskForm(prev => ({ ...prev, contribution_value: e.target.value }))}
                  placeholder="500000"
                  required
                  data-testid="detail-subtask-contribution-input"
                />
                {isPositiveDecimal(subtaskForm.contribution_value) && (
                  <p className="text-xs text-muted-foreground">
                    {formatNumericValue(subtaskForm.contribution_value, getNumericConfig(task))}
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>START DATE <span className="text-destructive">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{subtaskForm.start_date ? format(subtaskForm.start_date, 'MMM d, yyyy') : 'Pick a date'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={subtaskForm.start_date}
                      onSelect={(date) => {
                        setSubtaskForm(prev => {
                          const updated = { ...prev, start_date: date };
                          if (prev.end_date && date && prev.end_date < date) {
                            updated.end_date = null;
                          }
                          return updated;
                        });
                      }}
                      disabled={{ before: new Date() }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>END DATE <span className="text-destructive">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{subtaskForm.end_date ? format(subtaskForm.end_date, 'MMM d, yyyy') : 'Pick a date'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={subtaskForm.end_date}
                      onSelect={(date) => setSubtaskForm(prev => ({ ...prev, end_date: date }))}
                      disabled={{ before: subtaskForm.start_date || new Date() }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>PRIORITY</Label>
                <Select
                  value={subtaskForm.priority}
                  onValueChange={(value) => setSubtaskForm(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>ASSIGNED TO <span className="text-destructive">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <Users className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">
                        {(subtaskForm.additional_assignees || []).length > 0
                          ? `${subtaskForm.additional_assignees.length} selected`
                          : 'Select people'}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[240px] p-0" align="start">
                    <div className="max-h-48 overflow-y-auto p-1">
                      {activeUsers.length === 0 ? (
                        <p className="text-xs text-muted-foreground p-2 text-center">No users available</p>
                      ) : (
                        activeUsers.map(u => (
                          <div
                            key={u.id}
                            className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
                            onClick={() => toggleSubtaskAssignee(u.id)}
                          >
                            <div className={`h-4 w-4 border rounded-sm flex items-center justify-center shrink-0 ${
                              (subtaskForm.additional_assignees || []).includes(u.id) ? 'bg-primary border-primary' : 'border-muted-foreground'
                            }`}>
                              {(subtaskForm.additional_assignees || []).includes(u.id) && <Check className="h-3 w-3 text-primary-foreground" />}
                            </div>
                            <span className="text-sm">{u.name}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setSubtaskDialogOpen(false)}>
                CANCEL
              </Button>
              <Button type="submit" disabled={submittingSubtask}>
                {submittingSubtask && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                CREATE SUBTASK
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Task/Subtask Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSubtaskId ? 'EDIT SUBTASK' : 'EDIT TASK'}</DialogTitle>
            <DialogDescription>
              {editingSubtaskId ? 'Update the subtask details below' : 'Update the task details below'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">TITLE</Label>
              <Input
                id="edit-title"
                value={editFormData.title}
                onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
                required
                data-testid="edit-task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">DESCRIPTION</Label>
              <Textarea
                id="edit-description"
                value={editFormData.description}
                onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="edit-task-description-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>TASK TYPE</Label>
                <Input value={getTaskTypeLabel(editFormData.task_type)} disabled className="bg-muted" />
              </div>
              {isNumericalTask(editFormData.task_type) && !editingSubtaskId && (
                <div className="space-y-2">
                  <Label>CURRENCY</Label>
                  <Select
                    value={editFormData.numeric_config?.currency_code || 'INR'}
                    onValueChange={(value) => setEditFormData(prev => ({
                      ...prev,
                      numeric_config: { ...prev.numeric_config, currency_code: value },
                    }))}
                  >
                    <SelectTrigger data-testid="edit-task-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {NUMERICAL_CURRENCY_OPTIONS.map(currency => (
                        <SelectItem key={currency.code} value={currency.code}>{currency.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {!isNumericalTask(editFormData.task_type) && !editingSubtaskId && (
              <div className="flex items-start gap-3 border border-border p-3">
                <Checkbox
                  id="edit-task-ongoing"
                  checked={!!editFormData.is_ongoing}
                  onCheckedChange={(checked) => setEditFormData(prev => ({ ...prev, is_ongoing: checked === true }))}
                  className="mt-0.5"
                  data-testid="edit-task-ongoing-checkbox"
                />
                <div className="space-y-0.5">
                  <Label htmlFor="edit-task-ongoing" className="cursor-pointer">ONGOING TASK</Label>
                  <p className="text-xs text-muted-foreground">
                    Continuous responsibility with no % progress. The end date becomes an extendable Till Date.
                  </p>
                </div>
              </div>
            )}
            {isNumericalTask(editFormData.task_type) && !editingSubtaskId && (
              <div className="space-y-2">
                <Label>TARGET AMOUNT <span className="text-destructive">*</span></Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  pattern={"\\d+(\\.\\d{1,2})?"}
                  value={editFormData.numeric_config?.target_value || ''}
                  onChange={(e) => setEditFormData(prev => ({
                    ...prev,
                    numeric_config: { ...prev.numeric_config, target_value: e.target.value },
                  }))}
                  required
                  data-testid="edit-task-target-value-input"
                />
                {isPositiveDecimal(editFormData.numeric_config?.target_value) && (
                  <p className="text-xs text-muted-foreground">
                    {formatNumericValue(editFormData.numeric_config.target_value, editFormData.numeric_config)}
                  </p>
                )}
              </div>
            )}
            {isNumericalTask(editFormData.task_type) && editingSubtaskId && (
              <div className="space-y-2">
                <Label>CONTRIBUTION AMOUNT <span className="text-destructive">*</span></Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  pattern={"\\d+(\\.\\d{1,2})?"}
                  value={editFormData.contribution_value || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, contribution_value: e.target.value }))}
                  required
                  data-testid="edit-subtask-contribution-input"
                />
                {isPositiveDecimal(editFormData.contribution_value) && (
                  <p className="text-xs text-muted-foreground">
                    {formatNumericValue(editFormData.contribution_value, editFormData.numeric_config)}
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>START DATE <span className="text-destructive">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{editFormData.start_date ? format(editFormData.start_date, 'MMM d, yyyy') : 'Pick a date'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={editFormData.start_date}
                      onSelect={(date) => {
                        setEditFormData(prev => {
                          const updated = { ...prev, start_date: date };
                          if (prev.end_date && date && prev.end_date < date) {
                            updated.end_date = null;
                          }
                          return updated;
                        });
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>{editFormData.is_ongoing ? 'TILL DATE' : 'END DATE'} <span className="text-destructive">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{editFormData.end_date ? format(editFormData.end_date, 'MMM d, yyyy') : 'Pick a date'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={editFormData.end_date}
                      onSelect={(date) => setEditFormData(prev => ({ ...prev, end_date: date }))}
                      disabled={editFormData.start_date ? { before: editFormData.start_date } : undefined}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>PRIORITY</Label>
                <Select
                  value={editFormData.priority}
                  onValueChange={(value) => setEditFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger data-testid="edit-task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Parent tasks support editing the primary owner. */}
              {!editingSubtaskId && (
                <div className="space-y-2">
                  <Label>PRIMARY OWNER <span className="text-destructive">*</span></Label>
                  <Select
                    value={editFormData.primary_assignee || ''}
                    onValueChange={(value) => setEditFormData(prev => ({
                      ...prev,
                      primary_assignee: value,
                      secondary_assignee: prev.secondary_assignee === value ? '' : prev.secondary_assignee,
                      additional_assignees: (prev.additional_assignees || []).filter(id => id !== value),
                    }))}
                  >
                    <SelectTrigger data-testid="edit-task-primary-assignee-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
                    <SelectContent>
                      {editOwnerOptions.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}{u.is_active === false ? ' (Inactive)' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* For subtasks: show assigned to in the second column */}
              {editingSubtaskId && (
                <div className="space-y-2">
                  <Label>ASSIGNED TO</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <Users className="mr-2 h-4 w-4 shrink-0" />
                        <span className="truncate">
                          {(editFormData.additional_assignees || []).length > 0
                            ? `${editFormData.additional_assignees.length} selected`
                            : 'Select people'}
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[240px] p-0" align="start">
                      <div className="max-h-48 overflow-y-auto p-1">
                        {activeUsers.length === 0 ? (
                          <p className="text-xs text-muted-foreground p-2 text-center">No users available</p>
                        ) : (
                          activeUsers.map(u => (
                            <div
                              key={u.id}
                              className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
                              onClick={() => toggleEditAssignee(u.id)}
                            >
                              <div className={`h-4 w-4 border rounded-sm flex items-center justify-center shrink-0 ${
                                (editFormData.additional_assignees || []).includes(u.id) ? 'bg-primary border-primary' : 'border-muted-foreground'
                              }`}>
                                {(editFormData.additional_assignees || []).includes(u.id) && <Check className="h-3 w-3 text-primary-foreground" />}
                              </div>
                              <span className="text-sm">{u.name}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
            {/* For parent tasks: secondary owner + assigned to */}
            {!editingSubtaskId && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SECONDARY OWNER</Label>
                  <Select
                    value={editFormData.secondary_assignee}
                    onValueChange={(value) => {
                      setEditFormData(prev => {
                        const updated = { ...prev, secondary_assignee: value };
                        if (value && value !== 'none') {
                          updated.additional_assignees = (prev.additional_assignees || []).filter(id => id !== value);
                        }
                        return updated;
                      });
                    }}
                  >
                    <SelectTrigger data-testid="edit-task-secondary-assignee-select">
                      <SelectValue placeholder="Select person" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {editOwnerOptions.filter(u => u.id !== editPrimaryAssigneeId).map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}{u.is_active === false ? ' (Inactive)' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>ASSIGNED TO</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <Users className="mr-2 h-4 w-4 shrink-0" />
                        <span className="truncate">
                          {(editFormData.additional_assignees || []).length > 0
                            ? `${editFormData.additional_assignees.length} selected`
                            : 'Select people'}
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[240px] p-0" align="start">
                      <div className="max-h-48 overflow-y-auto p-1">
                        {editAvailableForAssignment.length === 0 ? (
                          <p className="text-xs text-muted-foreground p-2 text-center">No users available</p>
                        ) : (
                          editAvailableForAssignment.map(u => (
                            <div
                              key={u.id}
                              className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
                              onClick={() => toggleEditAssignee(u.id)}
                            >
                              <div className={`h-4 w-4 border rounded-sm flex items-center justify-center shrink-0 ${
                                (editFormData.additional_assignees || []).includes(u.id) ? 'bg-primary border-primary' : 'border-muted-foreground'
                              }`}>
                                {(editFormData.additional_assignees || []).includes(u.id) && <Check className="h-3 w-3 text-primary-foreground" />}
                              </div>
                              <span className="text-sm">{u.name}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}
            <DriveLinksEditor
              value={editFormData.drive_links || []}
              onChange={(links) => setEditFormData(prev => ({ ...prev, drive_links: links }))}
              disabled={submittingEdit}
            />
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setEditDialogOpen(false)}>
                CANCEL
              </Button>
              <Button type="submit" disabled={submittingEdit} data-testid="edit-task-submit-btn">
                {submittingEdit && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                UPDATE
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Comment Dialog */}
      <ConfirmDialog
        open={deleteCommentConfirm.open}
        onOpenChange={(open) => {
          if (!open) setDeleteCommentConfirm({ open: false, commentId: null });
        }}
        title="Delete comment?"
        description="This action cannot be undone. The comment will be permanently removed."
        confirmLabel="DELETE"
        onConfirm={handleDeleteComment}
        loading={deleting}
      />

      {/* Confirm Delete Task Dialog */}
      <ConfirmDialog
        open={deleteTaskConfirm}
        onOpenChange={setDeleteTaskConfirm}
        title={isSubtask ? 'Delete subtask?' : 'Delete task?'}
        description={isSubtask
          ? 'This action cannot be undone. The subtask will be permanently removed.'
          : 'This action cannot be undone. The task and all its subtasks and comments will be permanently removed.'
        }
        confirmLabel="DELETE"
        onConfirm={handleDeleteTask}
        loading={deleting}
      />

      {/* Confirm Delete Subtask Dialog */}
      <ConfirmDialog
        open={deleteSubtaskConfirm.open}
        onOpenChange={(open) => {
          if (!open) setDeleteSubtaskConfirm({ open: false, subtaskId: null });
        }}
        title="Delete subtask?"
        description="This action cannot be undone. The subtask will be permanently removed."
        confirmLabel="DELETE"
        onConfirm={handleDeleteSubtask}
        loading={deletingSubtask}
      />
    </div>
  );
};

export default TaskDetailPage;
